/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';

import SchedulePlayer from './schedule-player';

/**
 * Strategie přehrávání pro samostatné (osiřelé) médium nebo šablonu.
 * Přidává k původní strategii vlastní časovač přehrávání.
 * V jiné situaci není doba přehrávání používána.
 * @implements {ScheduleStrategy}
 */
class ScheduleOrphan {
  /**
   * Vytvoří instanci.
   * @param {number} playDuration Doba přehrávání média nebo šablony. [ms]
   * @param {ScheduleStrategy} scheduleStrategy Strategie pro přehrávání bez časovače.
   * @returns {ScheduleOrphan} Nová instance.
   */
  constructor(playDuration, scheduleStrategy) {
    const schedulePlayer = new SchedulePlayer();
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.playDuration = playDuration;
    /**
     * Aktuální informace o přehrávání.
     * @private
     * @type {?ScheduleInfo}
     */
    this.scheduleInfo = null;
    this.schedulePlayer = schedulePlayer;
    this.scheduleStrategy = scheduleStrategy;
    // Navázání naslouchání na instanci.
    this.onGone = this.onGone.bind(this);
    this.onPlayed = this.onPlayed.bind(this);
    // Naslouchání událostem.
    schedulePlayer.on('played', this.onPlayed);
    scheduleStrategy.on('gone', this.onGone);
  }

  /** @inheritdoc */
  destroy() {
    const { schedulePlayer, scheduleStrategy } = this;
    schedulePlayer.off('played', this.onPlayed);
    schedulePlayer.destroy();
    scheduleStrategy.off('gone', this.onGone);
    scheduleStrategy.destroy();
  }

  // TODO #523: Někdo by očekával, že playDuration se bude mergovat v ScheduleRegion,
  // ale kvůli výjimce pro playlisty, které nemají explicitní výchozí délku přehrávání,
  // je to lepší dělat zde, kde i máme explicitně playDuration zadané v konstruktoru,
  // viz poznámka v ScheduleRegion. Je potřeba nahrazovat/přepisovat ve směru top-down,
  // ale ScheduleStrategy.go apod. to skládají bottom-up, takže je potřeba mít pro tuto
  // položku v ScheduleInfo.merge netypicky obrácenou strategií nahrazení, tj. první bere!
  /**
   * Určí informace pro přehrání média.
   * @param {ScheduleInfo} scheduleInfo Informace z podřízené strategie.
   * @private
   * @returns {ScheduleInfo} Informace pro přehrání média.
   */
  getInfo(scheduleInfo) {
    return scheduleInfo.merge({
      playDuration: this.playDuration
    });
  }

  /** @inheritdoc */
  go() {
    const { schedulePlayer } = this;
    // Zastaví se případné přehrávání z předchozího cyklu.
    schedulePlayer.stop();
    // Spuštění nového přehrávání.
    schedulePlayer.play(this.playDuration);
    // Přechod na další slajd.
    return this.getInfo(this.scheduleStrategy.go());
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na přechod na další slajd.
   * @param {ScheduleInfo} scheduleInfo Informace potřebné k přehrání slajdu, na které byl proveden přechod.
   * @private
   * @returns {undefined} Nic.
   */
  onGone(scheduleInfo) {
    const toScheduleInfo = this.getInfo(scheduleInfo);
    this.scheduleInfo = toScheduleInfo;
    this.emitter.emit('gone', toScheduleInfo);
  }

  /**
   * Reakce na přehrání slajdu.
   * @private
   * @returns {undefined} Nic.
   */
  onPlayed() {
    this.emitter.emit('played', this.scheduleInfo);
  }

  /** @inheritdoc */
  plan(offset) {
    return this.scheduleStrategy
      .plan(offset)
      .map((childScheduleInfo) => this.getInfo(childScheduleInfo));
  }

  /** @inheritdoc */
  skip() {
    this.scheduleStrategy.skip();
  }

  /** @inheritdoc */
  stop() {
    this.schedulePlayer.stop();
  }

  /** @inheritdoc */
  toString() {
    return `ScheduleOrphan {scheduleStrategy: ${this.scheduleStrategy}}`;
  }
}

export default ScheduleOrphan;
